import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mic, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

// Track attempted tokens at module level to survive React Strict Mode remounts
const attemptedTokens = new Set();

const MagicLinkVerifyPage = () => {
  const [searchParams] = useSearchParams();
  const { verifyMagicLink, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const verificationInProgress = useRef(false);

  // Get token once on mount to avoid dependency issues
  const token = searchParams.get('token');

  useEffect(() => {
    // Check module-level Set to prevent duplicate API calls (survives Strict Mode)
    if (!token || attemptedTokens.has(token) || verificationInProgress.current) {
      if (!token) {
        setStatus('error');
        setError('Invalid magic link - no token provided');
      }
      return;
    }

    // If already authenticated, just redirect
    if (isAuthenticated) {
      setStatus('success');
      navigate('/dashboard', { replace: true });
      return;
    }

    // Mark token as attempted BEFORE any async work
    attemptedTokens.add(token);
    verificationInProgress.current = true;

    const verify = async () => {
      try {
        const result = await verifyMagicLink(token);

        if (result.success) {
          setStatus('success');
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 1500);
        } else {
          setStatus('error');
          setError(result.error || 'Failed to verify magic link');
        }
      } catch (err) {
        setStatus('error');
        setError(err.message || 'An unexpected error occurred');
      } finally {
        verificationInProgress.current = false;
      }
    };

    verify();
  // Only depend on token and isAuthenticated - verifyMagicLink/navigate are stable enough
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAuthenticated]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
            <Mic className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Alfred Voice Platform</h1>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {status === 'verifying' && (
            <div className="text-center">
              <LoadingSpinner size="lg" className="mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Verifying your login...
              </h2>
              <p className="text-gray-600">
                Please wait while we securely log you in.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Login Successful!
              </h2>
              <p className="text-gray-600">
                Redirecting you to your dashboard...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Verification Failed
              </h2>
              <p className="text-red-600 mb-6">
                {error}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  Back to Login
                </button>
                <p className="text-sm text-gray-600">
                  The magic link may have expired or already been used.
                  <br />
                  Please request a new one.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MagicLinkVerifyPage;
